import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Package, ShieldCheck, ShieldOff, ChevronRight, WalletCards, Users } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { useApi, fmtDate } from "@/lib/api";
import { useApp } from "@/lib/app-context";
import { ProductIcon, Spinner, StatusBadge } from "@/components/ww/Misc";
import { CalendarExport } from "@/components/ww/CalendarExport";
import { SpendingSummary } from "@/components/ww/SpendingSummary";
import { JOIN_KEY } from "./FamilyJoin";

const FILTERS = [["all", "All Products"], ["active", "Active"], ["expiring", "Expiring Soon"], ["expired", "Expired"]];
const SORTS = [["recent", "Recently Added"], ["purchase", "Purchase Date"], ["expiry", "Nearest Expiry"], ["name", "Product Name"]];

const Metric = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="rounded-[22px] border border-ww-border bg-white p-6" data-testid={testid}>
    <div className="flex items-center justify-between"><p className="text-sm font-medium text-ww-muted">{label}</p><Icon className="h-5 w-5 text-ww-emerald" /></div>
    <p className="mt-3 font-display text-4xl font-semibold text-ww-navy">{value}</p>
    <p className="mt-1 text-xs text-ww-muted">{sub}</p>
  </div>
);

const RING = { active: "#08A881", expiring: "#D97706", expired: "#DC2626" };

function ExpiryRing({ p }) {
  const live = p.coverages.filter((c) => c.status !== "expired").sort((a, b) => a.days_left - b.days_left)[0];
  const total = live ? Math.max(1, (new Date(live.expiry_date) - new Date(live.start_date)) / 86400000) : 1;
  const days = live ? live.days_left : 0;
  const pct = live ? Math.min(1, Math.max(0, days / total)) : 0;
  const r = 22;
  const c = 2 * Math.PI * r;
  const color = RING[p.status] || RING.active;
  const big = days >= 1000;
  return (
    <div className="relative h-14 w-14 shrink-0" title={live ? `${days} days until next expiry` : "All coverage expired"} data-testid={`product-expiry-ring-${p.id}`}>
      <svg viewBox="0 0 52 52" className="h-14 w-14 -rotate-90">
        <circle cx="26" cy="26" r={r} fill="none" stroke="#E5EAF0" strokeWidth="4" />
        <circle cx="26" cy="26" r={r} fill="none" stroke={color} strokeWidth="4" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)} className="transition-[stroke-dashoffset] duration-1000 ease-out" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className={`font-display font-semibold text-ww-navy ${big ? "text-[11px]" : "text-sm"}`} data-testid={`product-expiry-days-${p.id}`}>{live ? days : "0"}</span>
        <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wide text-ww-muted">{days === 1 ? "day" : "days"}</span>
      </div>
    </div>
  );
}

function HouseholdWallets({ items }) {
  if (!items.length) return null;
  return (
    <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="household-wallets">
      <span className="flex items-center gap-1.5 text-sm font-medium text-ww-muted"><Users className="h-4 w-4 text-ww-emerald" />Shared with you:</span>
      {items.map((l) => <Link key={l.id} to={`/shared/${l.owner_id}`} className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-ww-navy ring-1 ring-ww-border transition-colors hover:bg-ww-mint" data-testid={`household-wallet-${l.owner_id}`}>{l.owner_name}'s Wallet</Link>)}
    </div>
  );
}

export function ProductCard({ p, to }) {
  return (
    <Link to={to || `/products/${p.id}`} className="group flex flex-col rounded-[24px] border border-ww-border bg-white p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_26px_50px_-30px_rgba(16,43,64,0.45)]" data-testid={`product-card-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ww-mint text-ww-emerald"><ProductIcon name={p.name} /></span>
        <div className="flex flex-col items-end gap-2">
          <StatusBadge status={p.status} testid={`product-status-${p.id}`} />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3 rounded-2xl bg-ww-cyan/50 p-3">
        <ExpiryRing p={p} />
        <p className="text-xs text-ww-muted">{p.nearest_days_left == null ? "All coverage periods have expired" : p.nearest_days_left === 0 ? "Next coverage expires today" : <>Next coverage expires in <span className="font-semibold text-ww-navy">{p.nearest_days_left} {p.nearest_days_left === 1 ? "day" : "days"}</span></>}</p>
      </div>
      <p className="mt-5 truncate font-display text-lg font-semibold text-ww-navy">{p.name}</p>
      <p className="truncate text-sm text-ww-muted">{p.brand}{p.model_number ? ` · ${p.model_number}` : ""}</p>
      <div className="mt-5 grid grid-cols-2 gap-3 border-t border-ww-border pt-4 text-sm">
        <div><p className="text-xs text-ww-muted">Coverage periods</p><p className="font-semibold text-ww-navy">{p.coverage_count}</p></div>
        <div><p className="text-xs text-ww-muted">{p.nearest_expiry ? "Nearest expiry" : "Last expired"}</p><p className="font-semibold text-ww-navy">{fmtDate(p.nearest_expiry || p.last_expiry)}</p></div>
      </div>
      <span className="mt-5 flex items-center gap-1 text-sm font-semibold text-ww-emerald">View Details <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
    </Link>
  );
}

export default function Wallet() {
  const api = useApi();
  const navigate = useNavigate();
  const { me } = useApp();
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [data, setData] = useState(null);
  const [shared, setShared] = useState([]);
  useEffect(() => {
    const join = localStorage.getItem(JOIN_KEY);
    if (join) { navigate(`/family/join?token=${encodeURIComponent(join)}`); return; }
    api("get", "/family").then((r) => setShared(r.shared_with_me)).catch(() => setShared([]));
  }, [api, navigate]);
  useEffect(() => {
    const t = setTimeout(() => api("get", "/products", undefined, { params: { status, q, sort } }).then(setData).catch(() => setData({ products: [], metrics: {} })), 200);
    return () => clearTimeout(t);
  }, [api, status, q, sort]);
  const m = data?.metrics || {};
  const empty = data && m.saved === 0;
  return (
    <main className="mx-auto max-w-7xl px-5 py-12 lg:px-8" data-testid="wallet-page">
      <div className="flex flex-col justify-between gap-5 sm:flex-row sm:items-end">
        <div>
          <h1 className="font-display text-4xl font-semibold tracking-tight text-ww-navy sm:text-5xl">My Wallet</h1>
          <p className="mt-2 text-ww-muted">Your products, documents and coverage in one place.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {m.saved > 0 && <CalendarExport />}
          <button onClick={() => navigate("/add")} className="ww-btn-primary" data-testid="wallet-add-product-button"><Plus className="h-4 w-4" /> Add Product</button>
        </div>
      </div>

      <HouseholdWallets items={shared} />

      <div className="mt-10 grid gap-5 lg:grid-cols-4">
        <Metric testid="metric-saved-products" icon={Package} label="Saved Products" value={m.saved ?? "–"} sub="Total product records" />
        <Metric testid="metric-active-coverage" icon={ShieldCheck} label="Active Coverage" value={m.active ?? "–"} sub="With at least one active period" />
        <Metric testid="metric-expired" icon={ShieldOff} label="Expired" value={m.expired ?? "–"} sub="All periods expired" />
        <div className="rounded-[22px] bg-ww-navy p-6 text-white" data-testid="plan-info-card">
          <div className="flex items-center justify-between"><p className="text-sm text-white/70">Current Plan</p><WalletCards className="h-5 w-5 text-[#7FE8D3]" /></div>
          <p className="mt-3 font-display text-2xl font-semibold" data-testid="plan-info-summary">{me.plan_name} — {me.used} of {me.limit} records used</p>
          <Progress value={(me.used / me.limit) * 100} className="mt-3 h-1.5 bg-white/15 [&>div]:bg-ww-bright" />
          <div className="mt-3 flex items-center justify-between text-xs text-white/70">
            <span data-testid="plan-info-status">{me.subscription ? `Active until ${fmtDate(me.subscription.end_date)}` : "Free forever"}</span>
            <Link to="/account?tab=subscription" className="font-semibold text-[#7FE8D3] hover:underline" data-testid="manage-plan-button">Manage Plan</Link>
          </div>
        </div>
      </div>

      {m.saved > 0 && <SpendingSummary refreshKey={m.saved} />}

      {!data ? <Spinner /> : empty ? (
        <div className="mt-12 rounded-[28px] border border-dashed border-ww-emerald/30 bg-[linear-gradient(160deg,#ffffff,#EAF9F4)] px-6 py-20 text-center" data-testid="wallet-empty-state">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white text-ww-emerald shadow"><WalletCards className="h-8 w-8" /></span>
          <h2 className="mt-6 font-display text-2xl font-semibold text-ww-navy">Your Wallet Is Ready</h2>
          <p className="mx-auto mt-2 max-w-md text-ww-muted">Start by saving your first purchase invoice, warranty or guarantee card.</p>
          <button onClick={() => navigate("/add")} className="ww-btn-primary mt-8" data-testid="empty-add-first-product-button"><Plus className="h-4 w-4" /> Add Your First Product</button>
        </div>
      ) : (
        <>
          <div className="mt-12 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex gap-2 overflow-x-auto pb-1" role="tablist">
              {FILTERS.map(([v, l]) => (
                <button key={v} onClick={() => setStatus(v)} className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${status === v ? "bg-ww-navy text-white" : "bg-white text-ww-navy/80 ring-1 ring-ww-border hover:bg-ww-mint"}`} data-testid={`filter-${v}`}>{l}</button>
              ))}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ww-muted" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, brand or model" className="ww-input w-full pl-9 sm:w-72" data-testid="wallet-search-input" /></div>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="h-11 w-full rounded-xl border-ww-border bg-white sm:w-48" data-testid="wallet-sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`sort-option-${v}`}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="product-grid">
            {data.products.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
          {data.products.length === 0 && <p className="py-16 text-center text-ww-muted" data-testid="wallet-no-results">No products match your filters.</p>}
        </>
      )}
    </main>
  );
}
